// The venue and organisation console's routes (spec/13 §1): `/v/{venueSlug}/…` for a venue,
// `/o/{orgSlug}/…` for an organisation, and the sign-in and venue choice in front of them. A later
// screen is one more child route of the venue or organisation layout and one more item in its sidebar
// (`shell/layouts.tsx`). There is no HQ route here: HQ is its own build (`src/hq/routes.tsx`).
//
// Three more public routes sit beside sign-in (F-IAM-02, F-IAM-08, ADR-0033): `/sign-up`,
// `/display/{token}` (a staff-run station's countdown screen) and `/invitations/accept` — the organisation console's alone (the invitation link is built from
// `CONSOLE_URL`, never `HQ_CONSOLE_URL`), so they are registered here rather than in
// `routes/shared.tsx`, which the HQ build also uses.

import { createRoute, redirect } from '@tanstack/react-router';

import { fetchMyOrganizations, fetchMyVenues } from '../api/me';
import { queryKeys } from '../api/queries';
import { InvitationAcceptPage } from '../auth/InvitationAcceptPage';
import { SignUpPage } from '../auth/SignUpPage';
import { DisplayPage } from '../display/DisplayPage';
import { FloorArrangePage } from '../floor/FloorArrangePage';
import { FloorPage } from '../floor/FloorPage';
import { createSharedRoutes, readAuthSearch } from '../routes/shared';
import { AccountPage } from '../account/AccountPage';
import { AddonsPage } from './addons/AddonsPage';
import { BootPage, bootPageOfLegacyTab, bootSearchOf, readBootSearch } from '../boot/BootPage';
import { AuditLogPage } from './audit/AuditLogPage';
import { KitchenPage } from './kitchen/KitchenPage';
import { LibraryPage } from './library/LibraryPage';
import { MenuPage } from './menu/MenuPage';
import { OrdersPage } from './orders/OrdersPage';
import { PlayersPage } from './players/PlayersPage';
import { PricingPage } from './pricing/PricingPage';
import { readReportSearch } from './reports/report-search';
import { ReportsPage } from './reports/ReportsPage';
import { SellPage } from './sell/SellPage';
import { StaffPage } from './staff/StaffPage';
import { CountPage } from './stock/CountPage';
import { readStockSearch, StockPage } from './stock/StockPage';
import { VenuesPage } from './venues/VenuesPage';
import { ShiftsPage } from './shifts/ShiftsPage';
import { OrganizationSettingsPage, VenueSettingsPage } from '../settings/SettingsPage';
import { OfflinePlayPage } from './sessions/OfflinePlayPage';
import { GuestsPage } from './guests/GuestsPage';
import { readStationsSearch, StationsPage } from '../stations/StationsPage';
import { ChooseVenuePage } from '../shell/ChooseVenuePage';
import { ComingSoonPage } from '../shell/ComingSoon';
import { OrganizationLayout, VenueLayout } from '../shell/layouts';
import { NotFound } from '../shell/NotFound';
import { isOwnerRole, organizationHasOpened, setupLandingFor } from '../setup/landing';
import { OrganizationSetupPage } from '../setup/OrganizationSetupPage';
import { VenueSetupPage } from '../setup/VenueSetupPage';

export function createOrgRouteTree() {
  const { rootRoute, signInRoute, signedInRoute, welcomeRoute } = createSharedRoutes({ notFound: NotFound });

  // As `/sign-in`'s own (`routes/shared.tsx`): back to where the visitor was going, only somewhere in
  // this console — a path, never `//host` — and what a refused Google round trip came back with.
  const signUpRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/sign-up',
    validateSearch: readAuthSearch,
    component: SignUpPage,
  });

  const invitationAcceptRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/invitations/accept',
    component: InvitationAcceptPage,
  });

  // ADR-0033: a staff-run station's countdown screen — public and read-only, for a TV or tablet; the token is the
  // credential, so it sits beside sign-in rather than behind it.
  const displayRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/display/$token',
    component: DisplayPage,
  });

  /**
   * "Then choose a venue if the member has several" (spec/13 §1); one venue is opened directly. An owner whose
   * organisation has not opened a venue yet lands on its Setup instead (spec/13 §13.1, F-HQ-03).
   */
  const homeRoute = createRoute({
    getParentRoute: () => signedInRoute,
    path: '/',
    beforeLoad: async ({ context }) => {
      const venues = await context.queryClient
        .ensureQueryData({ queryKey: queryKeys.myVenues, queryFn: () => fetchMyVenues(context.api) })
        .catch(() => undefined);
      const organizations = await context.queryClient
        .ensureQueryData({ queryKey: queryKeys.myOrganizations, queryFn: () => fetchMyOrganizations(context.api) })
        .catch(() => undefined);
      const landing = venues === undefined || organizations === undefined ? undefined : setupLandingFor(organizations, venues);
      if (landing !== undefined) throw redirect({ to: '/o/$orgSlug/setup', params: { orgSlug: landing.slug } });
      const only = venues?.length === 1 ? venues[0] : undefined;
      if (only !== undefined) throw redirect({ to: '/v/$venueSlug', params: { venueSlug: only.slug } });
      throw redirect({ to: '/venues' });
    },
  });

  const venuesRoute = createRoute({ getParentRoute: () => signedInRoute, path: '/venues', component: ChooseVenuePage });

  // -------------------------------------------------------------------------------------------
  // A venue: /v/{venueSlug}/…
  // -------------------------------------------------------------------------------------------

  const venueRoute = createRoute({ getParentRoute: () => signedInRoute, path: '/v/$venueSlug', component: VenueLayout });

  /** The venue's landing screen is the live Floor (spec/13 §3, F-STN-14). */
  const venueIndexRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/',
    component: FloorPage,
  });

  /** The venue's Setup (spec/13 §13.1, F-HQ-03): its steps, essentials first, and Go live. */
  const venueSetupRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/setup',
    component: VenueSetupPage,
  });

  /** The settings landing (spec/13 §13): its setup summary, the search and every group. */
  const venueSettingsIndexRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/settings',
    component: VenueSettingsPage,
  });

  const venueSettingsRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/settings/$group',
    component: VenueSettingsPage,
  });

  /** Arrange mode (F-STN-19, spec/13 §3.4): the floor plan editor, `floor:arrange`. */
  const venueFloorArrangeRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/floor/arrange',
    component: FloorArrangePage,
  });

  /** Stations (spec/13 §12): its tab in the address — `?tab=classes`, `?tab=zones` — so a link lands on it. */
  const venueStationsRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/stations',
    validateSearch: readStationsSearch,
    component: StationsPage,
  });

  /**
   * Sessions had their own screen until 2026-10-07; they are run from the Floor now (its List view, the station modal),
   * so the old link opens the Floor.
   */
  const venueSessionsRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/sessions',
    beforeLoad: ({ params }) => {
      throw redirect({ to: '/v/$venueSlug', params: { venueSlug: params.venueSlug }, replace: true });
    },
  });

  /** Offline play (spec/13 §3.6; spec/05 §8): staff codes to print, and the sessions PCs ran offline to settle — from the Floor. */
  const venueOfflinePlayRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/sessions/offline',
    component: OfflinePlayPage,
  });

  /** Guests (F-SES-07): the venue's guest passes in use — ticket codes, time left, Void — from the Floor. */
  const venueGuestsRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/sessions/guests',
    component: GuestsPage,
  });

  /**
   * Sell (F-POS-09): the point of sale — one cart for everything a venue sells (spec/13 §4). The Passes tab's
   * Sell opens it with `?pass=<ppr_…>&player=<ply_…>` (F-POS-01): that pass in the cart, that player attached; the
   * station drawer adds `&session=<ses_…>` (F-SES-17), so a pass sold is added to the running session.
   */
  const venueSellRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/sell',
    validateSearch: (search: Record<string, unknown>): { pass?: string; player?: string; session?: string } => ({
      ...(typeof search['pass'] === 'string' && search['pass'].startsWith('ppr_') ? { pass: search['pass'] } : {}),
      ...(typeof search['player'] === 'string' && search['player'].startsWith('ply_') ? { player: search['player'] } : {}),
      // The station drawer's "Sell a pass" (F-SES-17): the player's running session, so the pass goes into it.
      ...(typeof search['session'] === 'string' && search['session'].startsWith('ses_') ? { session: search['session'] } : {}),
    }),
    component: SellPage,
  });

  /**
   * The Wallet screen until 2026-10-07: a player's wallet is run from their Wallet tab in Players now, and the kiosk's
   * pay-at-the-desk top-ups wait in the Floor's "What needs you" — so the old link opens Players.
   */
  const venueWalletRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/wallet',
    beforeLoad: ({ params }) => {
      throw redirect({ to: '/v/$venueSlug/players', params: { venueSlug: params.venueSlug }, replace: true });
    },
  });

  /** Players (F-ORG-05/-06): the organisation's players — search, profile, notes, tags, wallet, ban (spec/13 §8). */
  const venuePlayersRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/players',
    component: PlayersPage,
  });

  /** Staff and roles (F-IAM-08/-11, F-ORG-13): members, invitations and roles across the organisation. */
  const venueStaffRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/staff',
    component: StaffPage,
  });

  /** The live kitchen display (F-FNB-04/-05/-06): tickets by preparation station (spec/13 §6.2). */
  const venueKitchenRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/kitchen',
    component: KitchenPage,
  });

  /** Orders (F-POS-15): the venue's sales — list, detail and actions (spec/13 §5). */
  const venueOrdersRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/orders',
    component: OrdersPage,
  });

  /** The F&B menu setup (F-FNB-01/-02/-03): categories, products and the sold-out switch (spec/13 §6.1). */
  const venueMenuRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/menu',
    component: MenuPage,
  });

  /**
   * Stock (spec/13 §6.4, ADR-0040; F-FNB-07 … F-FNB-13): Items, Counts, Deliveries, Suppliers and Reports, one tab per
   * URL; `?level=` filters the list and `?item=` opens an item ("What needs you" links straight to one). Without a
   * tab, the list.
   */
  const venueStockIndexRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/stock',
    beforeLoad: ({ params }) => {
      throw redirect({ to: '/v/$venueSlug/stock/$tab', params: { venueSlug: params.venueSlug, tab: 'items' } });
    },
  });

  const venueStockRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/stock/$tab',
    validateSearch: readStockSearch,
    component: StockPage,
  });

  /** One count, counted on its own page: the sheet while it is open, its review once posted (spec/13 §6.4 Count). */
  const venueStockCountRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/stock/counts/$countId',
    component: CountPage,
  });

  /**
   * Shifts, cash and the day close (F-OPS-01 … F-OPS-07): open a till, move cash, close and reconcile (spec/13 §10).
   * `?shift=<shf_…>` opens that shift's detail — where a variance alert in "What needs you" leads (F-OPS-07).
   */
  const venueShiftsRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/shifts',
    // `?day=YYYY-MM-DD` shows that business day's report — a day that closed at its rollover, to sign off (ADR-0029).
    validateSearch: (search: Record<string, unknown>): { shift?: string; day?: string } => ({
      ...(typeof search['shift'] === 'string' && search['shift'].startsWith('shf_') ? { shift: search['shift'] } : {}),
      ...(typeof search['day'] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(search['day']) ? { day: search['day'] } : {}),
    }),
    component: ShiftsPage,
  });

  /**
   * Pricing & promotions (spec/13 §18): passes, the pay-as-you-go rate, promotions, codes and coupons,
   * and memberships (soon) — one tab per URL. Without a tab, the first (Passes).
   */
  const venuePricingIndexRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/pricing',
    beforeLoad: ({ params }) => {
      throw redirect({ to: '/v/$venueSlug/pricing/$tab', params: { venueSlug: params.venueSlug, tab: 'passes' } });
    },
  });

  const venuePricingRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/pricing/$tab',
    component: PricingPage,
  });

  /** Passes had their own screen until 2026-09-29; the old link opens the Passes tab. */
  const venuePassesRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/passes',
    beforeLoad: ({ params }) => {
      throw redirect({ to: '/v/$venueSlug/pricing/$tab', params: { venueSlug: params.venueSlug, tab: 'passes' }, replace: true });
    },
  });

  /** Library (F-CAT-02, spec/13 §9.1): the titles this venue offers, from the platform catalogue. */
  const venueLibraryRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/library',
    component: LibraryPage,
  });

  /**
   * Reports (spec/13 §2, §15, spec/17; F-ANL-03): tabs over a range of business days and a scope, all in the URL
   * (`?tab=&range=&from=&to=&venue=`) so a report is a link to share.
   */
  const venueReportsRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/reports',
    validateSearch: readReportSearch,
    component: ReportsPage,
  });

  /** Your account (spec/13 §2's staff menu): PIN, second factor, devices, notifications. */
  const venueAccountRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/account',
    component: AccountPage,
  });

  /**
   * PCs & games (spec/25 §16.1): the Boot server, Windows images and Games pages — its page in the address (`?page=images`).
   * An old `?tab=` link (Servers, Images, Health, Settings, PCs) is sent to the page that holds it.
   */
  const venueBootRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/boot',
    validateSearch: readBootSearch,
    beforeLoad: ({ params, search }) => {
      const page = bootPageOfLegacyTab(search.tab);
      if (page !== undefined) throw redirect({ to: '/v/$venueSlug/boot', params: { venueSlug: params.venueSlug }, search: bootSearchOf(page), replace: true });
    },
    component: BootPage,
  });

  /** The shared placeholder for a venue sidebar section not built yet (`shell/ComingSoon.tsx`). */
  const venueComingSoonRoute = createRoute({
    getParentRoute: () => venueRoute,
    path: '/soon/$section',
    component: ComingSoonPage,
  });

  // -------------------------------------------------------------------------------------------
  // An organisation: /o/{orgSlug}/…
  // -------------------------------------------------------------------------------------------

  const organizationRoute = createRoute({
    getParentRoute: () => signedInRoute,
    path: '/o/$orgSlug',
    component: OrganizationLayout,
  });

  /**
   * The organisation's landing: its Setup for an owner while no venue of it has opened (spec/13 §13.1, F-HQ-03) — where
   * accepting an owner's invitation lands — and its settings otherwise.
   */
  const organizationIndexRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/',
    beforeLoad: async ({ params, context }) => {
      const organizations = await context.queryClient
        .ensureQueryData({ queryKey: queryKeys.myOrganizations, queryFn: () => fetchMyOrganizations(context.api) })
        .catch(() => undefined);
      const venues = await context.queryClient
        .ensureQueryData({ queryKey: queryKeys.myVenues, queryFn: () => fetchMyVenues(context.api) })
        .catch(() => undefined);
      const organization = organizations?.find((candidate) => candidate.slug === params.orgSlug);
      if (organization !== undefined && venues !== undefined && isOwnerRole(organization.role) && !organizationHasOpened(organization.id, venues)) {
        throw redirect({ to: '/o/$orgSlug/setup', params: { orgSlug: params.orgSlug } });
      }
      throw redirect({ to: '/o/$orgSlug/settings', params: { orgSlug: params.orgSlug } });
    },
  });

  /** The organisation's Setup (spec/13 §13.1, F-HQ-03): its business details, its first venue, each venue's progress. */
  const organizationSetupRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/setup',
    component: OrganizationSetupPage,
  });

  /** Without a group, the first one the organisation's screen has. */
  const organizationSettingsIndexRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/settings',
    component: OrganizationSettingsPage,
  });

  const organizationSettingsRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/settings/$group',
    component: OrganizationSettingsPage,
  });

  /** The organisation's players (F-ORG-05/-06): the same screen as the venue's, spanning every venue. */
  const organizationPlayersRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/players',
    component: PlayersPage,
  });

  /** Staff and roles (F-IAM-08/-11, F-ORG-13): the organisation's members, invitations and roles. */
  const organizationStaffRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/staff',
    component: StaffPage,
  });

  /** What the organisation pays for add-ons, read-only (F-BOOT-17): `org:billing`. */
  const organizationAddonsRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/add-ons',
    component: AddonsPage,
  });

  /** The organisation's audit log (F-ORG-14, spec/13 §15): every recorded action, gated on `audit:read`. */
  const organizationAuditRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/audit',
    component: AuditLogPage,
  });

  /** The organisation's venues (F-ORG-01, spec/13 §13), and its franchisees' read-only (F-ORG-17). */
  const organizationVenuesRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/venues',
    component: VenuesPage,
  });

  /** Pricing & promotions at the organisation (spec/13 §18): the rate, promotions, codes and coupons. */
  const organizationPricingIndexRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/pricing',
    beforeLoad: ({ params }) => {
      throw redirect({ to: '/o/$orgSlug/pricing/$tab', params: { orgSlug: params.orgSlug, tab: 'payg' } });
    },
  });

  const organizationPricingRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/pricing/$tab',
    component: PricingPage,
  });

  /** Your account, from the organisation's staff menu. */
  const organizationAccountRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/account',
    component: AccountPage,
  });

  /** The same placeholder for an organisation sidebar section not built yet. */
  const organizationComingSoonRoute = createRoute({
    getParentRoute: () => organizationRoute,
    path: '/soon/$section',
    component: ComingSoonPage,
  });

  return rootRoute.addChildren([
    signInRoute,
    signUpRoute,
    welcomeRoute,
    invitationAcceptRoute,
    displayRoute,
    signedInRoute.addChildren([
      homeRoute,
      venuesRoute,
      venueRoute.addChildren([
        venueIndexRoute,
        venueSetupRoute,
        venueSettingsIndexRoute,
        venueSettingsRoute,
        venueFloorArrangeRoute,
        venueStationsRoute,
        venueBootRoute,
        venueSessionsRoute,
        venueOfflinePlayRoute,
        venueGuestsRoute,
        venueSellRoute,
        venueWalletRoute,
        venuePlayersRoute,
        venueStaffRoute,
        venueKitchenRoute,
        venueOrdersRoute,
        venueMenuRoute,
        venueStockIndexRoute,
        venueStockRoute,
        venueStockCountRoute,
        venueShiftsRoute,
        venuePassesRoute,
        venuePricingIndexRoute,
        venuePricingRoute,
        venueLibraryRoute,
        venueReportsRoute,
        venueAccountRoute,
        venueComingSoonRoute,
      ]),
      organizationRoute.addChildren([
        organizationIndexRoute,
        organizationSetupRoute,
        organizationSettingsIndexRoute,
        organizationSettingsRoute,
        organizationPlayersRoute,
        organizationStaffRoute,
        organizationAuditRoute,
        organizationAddonsRoute,
        organizationVenuesRoute,
        organizationPricingIndexRoute,
        organizationPricingRoute,
        organizationAccountRoute,
        organizationComingSoonRoute,
      ]),
    ]),
  ]);
}

export type OrgRouteTree = ReturnType<typeof createOrgRouteTree>;
